import { useEffect, useMemo, useState } from "react";
import { create_update_app, query_app } from "../api";
import { ActionButton, Drawer, Icon, Notice, Pager, StatusBadge, formatTime, messageOf, useRemoteList } from "./adminUi";

const PAGE_SIZE = 10;
const emptyShop = { app_name: "", shop_name: "", api_key: "", shared_secret: "" };

function ShopEditor({ shop, onClose, onSaved, canChangeDomain }) {
  const [values, setValues] = useState(() => ({ ...emptyShop, ...shop }));
  const [visible, setVisible] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [discard, setDiscard] = useState(false);
  const original = { ...emptyShop, ...shop };
  const dirty = Object.keys(emptyShop).some((key) => (values[key] || "") !== (original[key] || ""));
  const close = () => { if (saving) return; if (dirty) setDiscard(true); else onClose(); };
  const update = (key, value) => { setValues((previous) => ({ ...previous, [key]: value })); setError(""); setDiscard(false); };
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const domain = values.shop_name.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(domain)) {
      setError("Enter a Shopify domain such as store.myshopify.com, without https:// or a path."); return;
    }
    if (!values.api_key?.trim() || !values.shared_secret?.trim()) {
      setError("API key and shared secret are required."); return;
    }
    setSaving(true); setError("");
    try {
      const response = await create_update_app({ ...(shop.id ? { id: shop.id } : {}),
        app_name: values.app_name.trim(), shop_name: domain,
        api_key: values.api_key.trim(), shared_secret: values.shared_secret.trim() });
      if (response.status !== 200) throw new Error(response.msg || "Unable to save shop.");
      onSaved();
    } catch (err) { setError(messageOf(err, "Unable to save shop. Please try again.")); }
    finally { setSaving(false); }
  };
  return <Drawer title={shop.id ? "Edit shop" : "Add shop"} subtitle={shop.app_name || (shop.id ? shop.shop_name : "Connect a Shopify store")} onClose={close} busy={saving}
    footer={discard ? <><ActionButton onClick={() => setDiscard(false)}>Keep editing</ActionButton><ActionButton tone="danger" onClick={onClose}>Discard changes</ActionButton></>
      : <><ActionButton onClick={close} disabled={saving}>Cancel</ActionButton><button type="submit" form="shop-editor" className="sa-button sa-button--primary" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button></>}>
    <Notice>{error}</Notice>
    {discard && <Notice>You have unsaved changes. Discard them or keep editing.</Notice>}
    <form id="shop-editor" onSubmit={save}>
      <fieldset disabled={saving}>
        <section className="sa-form-section"><h3>Shop details</h3>
          <label className="sa-field">App name<input value={values.app_name || ""} onChange={(e) => update("app_name", e.target.value)} autoComplete="off" /></label>
          <label className="sa-field">Shop domain<input required disabled={Boolean(shop.id) && !canChangeDomain} value={values.shop_name || ""} onChange={(e) => update("shop_name", e.target.value)} placeholder="store.myshopify.com" autoComplete="off" spellCheck={false} /></label>
          {shop.id && !canChangeDomain && <p className="sa-muted">Changing the shop domain requires all-shops access.</p>}
          {shop.id && <div className="sa-field"><span>Installation status</span><div><StatusBadge deleted={shop.is_delete} /></div></div>}
        </section>
        <section className="sa-form-section"><h3>App credentials</h3><p className="sa-muted">Hidden by default.</p>
          {[["api_key", "API key"], ["shared_secret", "Shared secret"]].map(([key, label]) => <div className="sa-field" key={key}>
            <label htmlFor={key}>{label}</label><div className="sa-secret">
              <input id={key} required type={visible[key] ? "text" : "password"} value={values[key] || ""} onChange={(e) => update(key, e.target.value)} autoComplete="new-password" spellCheck={false} />
              <ActionButton icon="eye" aria-label={`${visible[key] ? "Hide" : "Show"} ${label}`} aria-pressed={Boolean(visible[key])} onClick={() => setVisible((previous) => ({ ...previous, [key]: !previous[key] }))} />
            </div>
          </div>)}
        </section>
      </fieldset>
    </form>
  </Drawer>;
}

export default function AppTable({ canManage = false, canCreate = false }) {
  const { items, loading, error, load } = useRemoteList(query_app, "Unable to load shops.");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  useEffect(() => { if (!canManage) setEditing(null); }, [canManage]);
  const installed = items.filter((shop) => Number(shop.is_delete) !== 1).length;
  const filtered = useMemo(() => items.filter((shop) => {
    const matchesStatus = status === "all" || (Number(shop.is_delete) === 1 ? "uninstalled" : "installed") === status;
    return matchesStatus && `${shop.app_name || ""} ${shop.shop_name || ""}`.toLowerCase().includes(query.trim().toLowerCase());
  }), [items, query, status]);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filter = (value) => { setStatus(value); setPage(1); };
  return <main className={`sa-workspace ${editing && canManage ? "sa-workspace--drawer" : ""}`}>
    <header className="sa-page-header"><div><h1>Shop settings</h1><p>Manage Shopify stores and app credentials.</p></div>
      <div className="sa-actions"><ActionButton icon="refresh" aria-label="Refresh shops" disabled={loading} onClick={load} />
        {canManage && canCreate && <ActionButton tone="primary" icon="plus" onClick={() => { setEditing({}); setNotice(""); }}>Add shop</ActionButton>}
      </div>
    </header>
    <Notice>{error}</Notice><Notice success>{notice}</Notice>
    <section className="sa-card" aria-label="Shops" aria-busy={loading}>
      <div className="sa-filter-tabs" role="group" aria-label="Installation status">
        {[["all", "All", items.length], ["installed", "Installed", installed], ["uninstalled", "Uninstalled", items.length - installed]].map(([key, label, count]) =>
          <button type="button" key={key} aria-pressed={status === key} className={status === key ? "is-active" : ""} onClick={() => filter(key)}>{label}<span>{count}</span></button>)}
      </div>
      <div className="sa-toolbar">
        <label className="sa-search"><Icon name="search" /><input type="search" aria-label="Search app or shop" placeholder="Search app or shop" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} /></label>
        <select aria-label="Shop status" value={status} onChange={(e) => filter(e.target.value)}><option value="all">All statuses</option><option value="installed">Installed</option><option value="uninstalled">Uninstalled</option></select>
      </div>
      <div className="sa-table-scroll"><table className="sa-table sa-shops-table">
        <thead><tr><th>Shop / App</th><th>Status</th><th>Updated (UTC)</th>{canManage && <th><span className="sa-sr-only">Actions</span></th>}</tr></thead>
        <tbody>{rows.map((shop, index) => <tr key={shop.id} className={editing?.id === shop.id ? "is-selected" : ""}>
          <td><div className="sa-shop-identity"><span className={`sa-shop-avatar sa-avatar-${index % 5}`}>{(shop.app_name || shop.shop_name || "S").slice(0, 1).toUpperCase()}</span>
            <div className="sa-identity-text"><strong title={shop.shop_name}>{shop.shop_name || "Unnamed shop"}</strong><span>{shop.app_name || "—"}</span></div></div></td>
          <td><StatusBadge deleted={shop.is_delete} /></td><td className="sa-date">{formatTime(shop.update_time)}</td>
          {canManage && <td><ActionButton icon="edit" aria-label={`Edit ${shop.shop_name}`} onClick={() => { setEditing(shop); setNotice(""); }} /></td>}
        </tr>)}</tbody>
      </table></div>
      {!rows.length && <div className="sa-empty" role="status">{loading ? "Loading shops…" : error ? "Shop data is unavailable. Use Refresh to try again." : items.length ? "No shops match your filters." : "No shops yet."}</div>}
      <Pager page={currentPage} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} noun="shops" />
    </section>
    {editing && canManage && <ShopEditor key={editing.id || "new"} shop={editing} canChangeDomain={canCreate} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setNotice("Shop saved."); load(); }} />}
  </main>;
}
